/**
 * @fileoverview Cloudflare Turnstile CAPTCHA widget for bot protection.
 *
 * Renders the invisible/managed Turnstile widget and provides the token
 * via callback. Uses Cloudflare's JS API (no npm package needed).
 *
 * In preview/dev, NEXT_PUBLIC_TURNSTILE_SITE_KEY should be set to
 * Cloudflare's dummy key "1x00000000000000000000AA" for auto-pass.
 *
 * @module components/TurnstileWidget
 */

"use client";

import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "invisible";
          action?: string;
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    __turnstileScriptLoading?: boolean;
    __turnstileScriptLoaded?: boolean;
  }
}

type TurnstileWidgetProps = {
  onToken: (token: string) => void;
  onError?: () => void;
  action?: string;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "invisible";
  className?: string;
};

export interface TurnstileWidgetHandle {
  reset: () => void;
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

/**
 * Cloudflare's dummy site key for development/testing.
 * When this key is used, Turnstile always passes verification without
 * making actual challenge requests (which would cause 400 errors in dev).
 * @see https://developers.cloudflare.com/turnstile/troubleshooting/testing/
 */
const TURNSTILE_DUMMY_KEY = "1x00000000000000000000AA";

/**
 * Check if Turnstile should be disabled (no key or dummy key).
 * Using dummy key in dev/preview causes 400 challenge errors in console,
 * so we skip the widget entirely and immediately return a mock token.
 */
function shouldSkipTurnstile(): boolean {
  return !SITE_KEY || SITE_KEY === TURNSTILE_DUMMY_KEY;
}

/**
 * Load Turnstile script globally with singleton pattern.
 * Prevents multiple script tags and race conditions.
 */
function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.turnstile) {
      resolve();
      return;
    }

    // Already loading - wait for it
    if (window.__turnstileScriptLoading) {
      const checkLoaded = () => {
        if (window.turnstile) {
          resolve();
        } else if (!window.__turnstileScriptLoading) {
          // Loading failed
          reject(new Error("Turnstile script failed to load"));
        } else {
          setTimeout(checkLoaded, 50);
        }
      };
      checkLoaded();
      return;
    }

    // Check if script tag already exists
    if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
      // Script exists but window.turnstile not ready yet - wait
      const checkReady = () => {
        if (window.turnstile) {
          resolve();
        } else {
          setTimeout(checkReady, 50);
        }
      };
      checkReady();
      return;
    }

    // Start loading
    window.__turnstileScriptLoading = true;

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => {
      window.__turnstileScriptLoading = false;
      window.__turnstileScriptLoaded = true;
      resolve();
    };
    script.onerror = () => {
      window.__turnstileScriptLoading = false;
      reject(new Error("Failed to load Turnstile script"));
    };

    document.head.appendChild(script);
  });
}

/**
 * Cloudflare Turnstile widget component.
 *
 * Loads the Turnstile script on mount and renders the challenge widget.
 * Calls onToken when a valid token is received.
 *
 * If NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set or is the dummy key,
 * renders nothing and immediately calls onToken with a mock token.
 * This prevents 400 challenge errors in development/preview environments.
 */
export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  ({ onToken, onError, action, theme = "auto", size = "normal", className }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [isReady, setIsReady] = useState(false);
    // Track if we're in skip mode to avoid rendering the container
    const [isSkipMode] = useState(() => shouldSkipTurnstile());

    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (isSkipMode) {
          // In skip mode, just re-trigger the mock token
          onTokenRef.current("dev-mode-mock-token");
          return;
        }
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    // Handle skip mode (dev/preview) - immediately return mock token
    useEffect(() => {
      if (isSkipMode) {
        onTokenRef.current("dev-mode-mock-token");
      }
    }, [isSkipMode]);

    // Handle script loading and widget rendering - only when not in skip mode
    useEffect(() => {
      if (isSkipMode) {
        return;
      }

      let cancelled = false;
      let rafId: number | null = null;

      const mountWidget = async () => {
        try {
          await loadTurnstileScript();
          
          if (cancelled || !containerRef.current) {
            return;
          }

          // Wait for next frame to ensure container is ready
          rafId = window.requestAnimationFrame(() => {
            if (cancelled || !containerRef.current || widgetIdRef.current) {
              return;
            }

            if (!window.turnstile) {
              return;
            }

            try {
              widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: SITE_KEY!,
                callback: (token: string) => onTokenRef.current(token),
                "error-callback": onError,
                "expired-callback": () => onTokenRef.current(""),
                theme,
                size,
                action,
              });
              setIsReady(true);
            } catch {
              // Widget render failed - don't spam console
              if (onError) {
                onError();
              }
            }
          });
        } catch {
          // Script load failed - silently fail in dev
          if (onError) {
            onError();
          }
        }
      };

      mountWidget();

      return () => {
        cancelled = true;
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
        }
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // Ignore cleanup errors
          }
          widgetIdRef.current = null;
        }
      };
    }, [action, isSkipMode, onError, size, theme]);

    // Don't render container when in skip mode
    if (isSkipMode) {
      return null;
    }

    return <div ref={containerRef} className={className} data-turnstile-container />;
  },
);
TurnstileWidget.displayName = "TurnstileWidget";
